import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Package,
  MapPin,
  Loader,
  Camera,
  ScanBarcode,
  X,
  Clock,
  TrendingDown,
  Zap,
  ArrowLeft,
  AlertCircle,
  MessageSquare,
  Plus,
  Lock,
  ExternalLink,
  Loader2,
  Share2,
  ImageUp,
} from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { canAddLead, isFreeTier } from "../utils/userUtils";
import {
  searchProduct,
  processQrCode,
  decodeBarcodePhoto,
  type StoreProductResult,
  type ProductSearchResponse,
} from "../services/ProductSearchService";
import { getAffiliateLink } from "../services/ProductsService";
import { createDeal, getDealShareUrl } from "../services/DealsService";
import {
  addHomeDepotTracker,
  isHomeDepotProductTracked,
  type AddHomeDepotTrackerRequest,
} from "../services/HomeDepotTrackingService";
import {
  addTrackerWithRetailer,
  isProductTrackedWithRetailer,
  type AddTrackerRequest,
} from "../services/TrackingService";
import {
  getSearchHistory,
  type SearchHistoryEntry,
  type SearchHistoryResponse,
} from "../services/SearchHistoryService";
import { getSearchProviders, type SearchProvider } from "../services/AppConfigService";
import { useRetailer } from "../hooks/useRetailer";
import { useEnrichedSearchResults } from "../hooks/useEnrichedSearchResults";
import NearbyStoreResultsList from "../components/NearbyStoreResultsList";
import VirtualizedStoreSearchList from "../components/VirtualizedStoreSearchList";
import { getStoreSearchDisplayPrice, type StoreSearchSort } from "../utils/storeSearchSort";
import type { HomeDepotSearchScope } from "../services/ProductSearchService";
import DiscordPriceModal from "../components/DiscordPriceModal";
import AddToLeadModal from "../components/AddToLeadModal";
import ProductSearchTrackingModal from "../components/ProductSearchTrackingModal";
import SaveButton from "../components/buttons/SaveButton";
import { toast } from "react-toastify";
import Webcam from "react-webcam";
import { BrowserMultiFormatReader } from "@zxing/library";
import Barcode from "react-barcode";
import {
  formatSearchErrorMessage,
  formatTimestamp,
  getStoredZipCode as getStoredZipCodeStorage,
  extractProductLink,
  identifyCodeType,
  parseSkuFromUrl,
  saveZipCode as saveZipCodeStorage,
  sortResultsByInventory,
} from "./ProductSearchPage.utils";
import { getRetailerDisplayName } from "../config/retailers";
import { samsClubStockLabel } from "../utils/stockCount";
import { buildRetailerLookupUrl, getRetailerLinkLabel } from "../utils/productUrls";
import { getProductImageUrl } from "../utils/imageUtils";

// Types
type SearchType = "UPC" | "SKU" | "URL" | "QR";
type DetectedCodeType = SearchType | "Unknown";

const getRetailerProductUrl = (retailer: string, sku: string): string => {
  return buildRetailerLookupUrl(retailer, sku);
};

const formatClientSearchDuration = (
  durationMs: number,
  t: (key: string, params?: Record<string, string | number>) => string,
): string =>
  durationMs < 1000
    ? t("searchPage.durationMs", { ms: durationMs })
    : t("searchPage.durationS", { s: (durationMs / 1000).toFixed(2) });



export default function ProductSearchPage() {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { t } = useTranslation();
  // Stable handle for memoized callbacks/effects that must not re-run when `t` identity changes
  const tRef = useRef(t);
  tRef.current = t;
  const retailer = useRetailer();
  const isFreeUser = isFreeTier(user);
  const showAddToLead = canAddLead(user);
  const isAdmin = !!user?.admin;
  const showProviderSelector = isAdmin && retailer === "walmart";
  const showHDScopeSelector = isAdmin && retailer === "home_depot";
  const [searchParams, setSearchParams] = useSearchParams();

  // Search form state
  const [code, setCode] = useState("");
  const [searchType, setSearchType] = useState<SearchType>("UPC");
  const [detectedCodeType, setDetectedCodeType] = useState<DetectedCodeType>("Unknown");
  const [zipCode, setZipCode] = useState("");
  const [isZipEditing, setIsZipEditing] = useState(false);

  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<StoreProductResult[]>([]);
  const enrichedSearchResults = useEnrichedSearchResults(searchResults, zipCode, retailer);
  const [storeSort, setStoreSort] = useState<StoreSearchSort>("best_deal");
  const [hdSearchScope, setHdSearchScope] = useState<HomeDepotSearchScope>("local");
  const [hdStoreFilter, setHdStoreFilter] = useState("");
  const [hdInStockOnly, setHdInStockOnly] = useState(false);
  const [searchMeta, setSearchMeta] = useState<{
    scope?: string;
    totalStores?: number;
    inStockCount?: number;
    searchDurationMs?: number;
  } | null>(null);
  const [clientSearchDurationMs, setClientSearchDurationMs] = useState<number | null>(null);
  const [amazonData, setAmazonData] = useState<{
    price?: number; title?: string; url?: string; asin?: string;
    rating?: number; reviews?: number; isPrime?: boolean; thumbnail?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isFreeTierSearchLockedError =
    !!error &&
    (error.toLowerCase().includes("not available on the free tier") ||
      error.toLowerCase().includes("basic and pro plans"));
  const nearbyLoadingTips = [
    t("products.nearbyOnHandTip"),
    t("products.nearbyLowOnHandNoAisleTip"),
  ];

  // Tracking state
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [isTracked, setIsTracked] = useState(false);
  const [isTrackingProduct, setIsTrackingProduct] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);

  // Camera/scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [isDecodingPhoto, setIsDecodingPhoto] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isWebcamReady, setIsWebcamReady] = useState(false);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);

  // Modals
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [showAddToLeadModal, setShowAddToLeadModal] = useState(false);
  const [discordProduct, setDiscordProduct] = useState<StoreProductResult | null>(null);
  const [leadProduct, setLeadProduct] = useState<StoreProductResult | null>(null);

  // Search history state
  const [searchHistory, setSearchHistory] = useState<SearchHistoryEntry[]>([]);
  const [historyNextCursor, setHistoryNextCursor] = useState<string | undefined>();
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Admin: Walmart search provider override
  const [searchProviders, setSearchProviders] = useState<SearchProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  // Admin: skip the backend Redis search cache
  const [bypassCache, setBypassCache] = useState(false);

  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const webcamRef = useRef<Webcam>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const historyObserverRef = useRef<IntersectionObserver | null>(null);
  const historyLoadMoreRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousViewKeyRef = useRef<string>("");
  const activeSearchControllerRef = useRef<AbortController | null>(null);
  const currentSearchRequestIdRef = useRef(0);
  const autoSearchHandledRef = useRef<string>("");

  // Derived values
  const firstSuccessfulResult = searchResults.find((r) => !r.error);
  const currentViewKey =
    isSearching || showSearchModal
      ? "search-loading"
      : searchResults.length > 0 || !!error
      ? "search-results"
      : "search-form";

  useEffect(() => {
    if (previousViewKeyRef.current && previousViewKeyRef.current !== currentViewKey) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
    }
    previousViewKeyRef.current = currentViewKey;
  }, [currentViewKey]);

  useEffect(() => {
    return () => {
      activeSearchControllerRef.current?.abort();
    };
  }, []);

  const applySearchResponse = (response: ProductSearchResponse) => {
    setSearchResults(sortResultsByInventory(response.results));
    setSearchMeta(
      response.scope === "nationwide"
        ? {
            scope: response.scope,
            totalStores: response.totalStores,
            inStockCount: response.inStockCount,
            searchDurationMs: response.searchDurationMs,
          }
        : null
    );
    if (response.amazonPrice != null && response.amazonPrice > 0) {
      setAmazonData({
        price: response.amazonPrice, title: response.amazonTitle,
        url: response.amazonURL, asin: response.amazonASIN,
        rating: response.amazonRating, reviews: response.amazonReviews,
        isPrime: response.amazonIsPrime, thumbnail: response.amazonThumbnail,
      });
    } else {
      setAmazonData(null);
    }
  };

  const isAbortError = (error: unknown) =>
    error instanceof DOMException
      ? error.name === "AbortError"
      : error instanceof Error
      ? error.name === "AbortError"
      : false;

  const runTimedProductSearch = useCallback(async (
    requestId: number,
    search: () => Promise<ProductSearchResponse>,
  ): Promise<ProductSearchResponse> => {
    const startedAt = performance.now();
    if (isAdmin && currentSearchRequestIdRef.current === requestId) {
      setClientSearchDurationMs(null);
    }
    try {
      return await search();
    } finally {
      if (isAdmin && currentSearchRequestIdRef.current === requestId) {
        setClientSearchDurationMs(Math.max(0, Math.round(performance.now() - startedAt)));
      }
    }
  }, [isAdmin]);

  const withProviderOption = (options: { signal?: AbortSignal } = {}) => ({
    ...options,
    ...(selectedProvider ? { provider: selectedProvider } : {}),
    ...(showProviderSelector && bypassCache ? { bypassCache: true } : {}),
    ...(showHDScopeSelector && hdSearchScope === "nationwide" ? { scope: "nationwide" as const } : {}),
  });


  // ============================================
  // Code Detection & Parsing
  // ============================================

  const handleCodeChange = (input: string) => {
    const newCode = extractProductLink(input);
    setCode(newCode);
    const detectedType = identifyCodeType(newCode, retailer);
    setDetectedCodeType(detectedType);
    if (detectedType !== "Unknown") {
      setSearchType(detectedType);
    }
  };

  const parseSkuFromUrlLocal = (url: string): string => {
    return parseSkuFromUrl(url, {
      invalidUrlFormat: t("productSearch.invalidUrlFormat"),
      invalidSkuFormat: t("productSearch.invalidSkuFormat"),
      urlParseError: t("productSearch.urlParseError"),
      failedToParseUrl: t("productSearch.failedToParseUrl"),
    });
  };

  // ============================================
  // Tracking Functions
  // ============================================

  const checkTrackingStatus = async (productUpc: string) => {
    try {
      if (retailer === "home_depot") {
        const tracked = await isHomeDepotProductTracked(productUpc);
        setIsTracked(tracked);
      } else {
        const tracked = await isProductTrackedWithRetailer(productUpc, retailer);
        setIsTracked(tracked);
      }
    } catch {
      setIsTracked(false);
    }
  };

  const handleTrackToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (searchResults.length === 0) {
      toast.error(t("searchPage.noResultsForTracking"));
      return;
    }

    setShowTrackingModal(true);
  };

  const handleAddTracker = async (data: {
    upc: string;
    zipCode: string;
    targetPrice: number;
    productName?: string;
    productImage?: string;
    productCategory?: string;
    msrp?: number;
    currentPrice?: number;
    itemId?: string;
  }) => {
    try {
      setIsTrackingProduct(true);

      if (retailer === "home_depot") {
        const homeDepotRequest: AddHomeDepotTrackerRequest = {
          itemId: data.itemId || data.upc,
          upc: data.itemId || data.upc,
          name: data.productName || "",
          image: data.productImage || "",
          category: data.productCategory || "",
          zipCode: data.zipCode,
          targetPrice: data.targetPrice,
          currentPrice: data.currentPrice || 0,
          msrp: data.msrp || 0,
        };
        await addHomeDepotTracker(homeDepotRequest);
      } else {
        const trackerRequest: AddTrackerRequest = {
          upc: data.upc,
          zipCode: data.zipCode,
          targetPrice: data.targetPrice,
        };
        await addTrackerWithRetailer(trackerRequest, retailer, user?.accountType, user?.maxAlertsOverride);
      }

      toast.success(t("searchPage.trackerAdded"));
      setShowTrackingModal(false);

      // Refresh tracking status
      if (firstSuccessfulResult?.sku) {
        checkTrackingStatus(firstSuccessfulResult.sku);
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("searchPage.trackerAddFailed"));
    } finally {
      setIsTrackingProduct(false);
    }
  };

  // ============================================
  // Search Functions
  // ============================================

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setError(t("searchPage.enterCode"));
      return;
    }

    if (!zipCode.trim()) {
      setError(t("searchPage.enterZip"));
      return;
    }

    if (detectedCodeType === "Unknown") {
      setError(t("searchPage.unableToDetectCode"));
      return;
    }

    activeSearchControllerRef.current?.abort();
    const controller = new AbortController();
    const requestId = currentSearchRequestIdRef.current + 1;
    currentSearchRequestIdRef.current = requestId;
    activeSearchControllerRef.current = controller;
    setIsSearching(true);
    setShowSearchModal(true);
    setError(null);
    setSearchResults([]);
    setSearchMeta(null);
    setStoreSort("best_deal");
    setHdStoreFilter("");

    try {
      let searchCode = code.trim();
      let actualSearchType: "UPC" | "SKU" = searchType === "URL" || searchType === "QR" ? "SKU" : searchType;

      // Parse URL to get SKU
      if (searchType === "URL") {
        try {
          searchCode = parseSkuFromUrlLocal(code.trim());
          actualSearchType = "SKU";
        } catch (parseError: unknown) {
          const message = parseError instanceof Error ? parseError.message : t("productSearch.failedToParseUrl");
          setError(message);
          setIsSearching(false);
          setShowSearchModal(false);
          return;
        }
      }

      // Process QR code to get SKU
      if (searchType === "QR") {
        try {
          searchCode = await processQrCode(code.trim(), { signal: controller.signal });
          actualSearchType = "SKU";
        } catch (parseError: unknown) {
          if (isAbortError(parseError)) return;
          const message = parseError instanceof Error ? parseError.message : t("productSearch.failedToProcessQr");
          setError(message);
          setIsSearching(false);
          setShowSearchModal(false);
          return;
        }
      }

      const response = await runTimedProductSearch(requestId, () =>
        searchProduct(searchCode, actualSearchType, zipCode.trim(), retailer, withProviderOption({ signal: controller.signal })),
      );
      if (currentSearchRequestIdRef.current !== requestId) return;
      applySearchResponse(response);


      // Check tracking status
      const successResult = response.results.find((r) => !r.error);
      if (successResult?.sku) {
        if (currentSearchRequestIdRef.current !== requestId) return;
        await checkTrackingStatus(successResult.sku);
      }
    } catch (err: unknown) {
      if (isAbortError(err)) return;
      if (currentSearchRequestIdRef.current !== requestId) return;
      const message = err instanceof Error ? err.message : t("searchPage.searchFailed");
      const formattedMessage = formatSearchErrorMessage(message);
      setError(formattedMessage);
      toast.error(formattedMessage);
    } finally {
      if (activeSearchControllerRef.current === controller) {
        activeSearchControllerRef.current = null;
        setIsSearching(false);
        setShowSearchModal(false);
      }
    }
  };

  const handleReset = () => {
    setCode("");
    setSearchResults([]);
    setSearchMeta(null);
    setClientSearchDurationMs(null);
    setHdStoreFilter("");
    setAmazonData(null);
    setError(null);
    setIsTracked(false);
    setIsTrackingProduct(false);
    setShowPriceDropdown(false);
    stopScanning();
    setIsWebcamReady(false);
    setCameraError(null);
    setShowDiscordModal(false);
    setDiscordProduct(null);
    setShowAddToLeadModal(false);
    setLeadProduct(null);
    setShowTrackingModal(false);
  };

  const handleBackToSearch = () => {
    currentSearchRequestIdRef.current += 1;
    activeSearchControllerRef.current?.abort();
    activeSearchControllerRef.current = null;
    setIsSearching(false);
    setSearchResults([]);
    setSearchMeta(null);
    setHdStoreFilter("");
    setAmazonData(null);
    setError(null);
    setIsTracked(false);
    setIsTrackingProduct(false);
    setShowPriceDropdown(false);
    setShowSearchModal(false);
    setShowDiscordModal(false);
    setDiscordProduct(null);
    setShowAddToLeadModal(false);
    setLeadProduct(null);
    setShowTrackingModal(false);
  };

  const handleViewHistoryResults = (item: SearchHistoryEntry) => {
    // Switch to search tab and show the cached results
    setCode(item.sku || "");
    setSearchType("SKU");
    setDetectedCodeType("SKU");
    setError(null);
    
    // Use the cached results from history
    if (item.results && item.results.length > 0) {
      setSearchResults(sortResultsByInventory(item.results));
      
      const successResult = item.results.find((r) => !r.error);
      if (successResult?.sku) {
        checkTrackingStatus(successResult.sku);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleSearchAgainFromHistory = async (item: SearchHistoryEntry) => {
    const searchCode = item.sku || "";
    if (!searchCode || !zipCode.trim()) {
      toast.error(t("searchPage.missingCodeOrZip"));
      return;
    }

    // Switch to search tab
    setCode(searchCode);
    setSearchType("SKU");
    setDetectedCodeType("SKU");
    setError(null);
    setSearchResults([]);
    setStoreSort("best_deal");

    // Perform a fresh search
    activeSearchControllerRef.current?.abort();
    const controller = new AbortController();
    const requestId = currentSearchRequestIdRef.current + 1;
    currentSearchRequestIdRef.current = requestId;
    activeSearchControllerRef.current = controller;
    setIsSearching(true);
    setShowSearchModal(true);

    try {
      const response = await runTimedProductSearch(requestId, () =>
        searchProduct(searchCode, "SKU", zipCode.trim(), retailer, withProviderOption({ signal: controller.signal })),
      );
      if (currentSearchRequestIdRef.current !== requestId) return;
      applySearchResponse(response);

      const successResult = response.results.find((r) => !r.error);
      if (successResult?.sku) {
        if (currentSearchRequestIdRef.current !== requestId) return;
        await checkTrackingStatus(successResult.sku);
      }
    } catch (err: unknown) {
      if (isAbortError(err)) return;
      if (currentSearchRequestIdRef.current !== requestId) return;
      const message = err instanceof Error ? err.message : t("searchPage.searchFailed");
      setError(message);
      toast.error(message);
    } finally {
      if (activeSearchControllerRef.current === controller) {
        activeSearchControllerRef.current = null;
        setIsSearching(false);
        setShowSearchModal(false);
      }
    }
  };

  // ============================================
  // Camera/Scanner Functions
  // ============================================

  const startScanning = async () => {
    setIsScanning(true);
    setError(null);
    setCameraError(null);
    setLastScannedCode(null);
  };

  const stopScanning = () => {
    setIsScanning(false);
    setError(null);
    setCameraError(null);
    setLastScannedCode(null);
  };

  const handleUserMedia = () => {
    setIsWebcamReady(true);
    setCameraError(null);
  };

  const handleUserMediaError = (error: string | DOMException) => {
    setIsWebcamReady(false);
    const errorMessage = error instanceof DOMException
      ? error.message || t("searchPage.cameraAccessDenied")
      : error || t("searchPage.cameraAccessDenied");
    setCameraError(errorMessage);
    setIsScanning(false);
    toast.error(t("searchPage.cameraError", { message: errorMessage }));
  };

  // Decode an uploaded barcode photo (ZXing locally, AI server fallback) and fill the input
  const handleBarcodePhoto = async (file: File) => {
    setIsDecodingPhoto(true);
    setError(null);
    try {
      const decoded = await decodeBarcodePhoto(file);
      handleCodeChange(decoded);
      toast.success(`${t("productSearch.barcodeScannedSuccess")} ${decoded}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("searchPage.photoDecodeFailed");
      toast.error(message);
    } finally {
      setIsDecodingPhoto(false);
    }
  };

  const captureAndScan = useCallback(() => {
    if (!isWebcamReady || !isScanning || !webcamRef.current || !readerRef.current) {
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;

    readerRef.current
      .decodeFromImage(undefined, imageSrc)
      .then(async (result) => {
        const scannedCode = result.getText();

        // Avoid duplicate scans
        if (lastScannedCode === scannedCode) return;
        setLastScannedCode(scannedCode);

        // Handle QR codes for Walmart/Home Depot
        if (searchType === "QR" && (scannedCode.includes("w-mt.co/q/") || scannedCode.includes("homedepot.com"))) {
          setIsScanning(false);
          setError(null);
          toast.success(t("productSearch.qrScanned"));
          const requestId = currentSearchRequestIdRef.current + 1;

          try {
            if (!zipCode.trim()) {
              setError(t("searchPage.enterZipToSearch"));
              setIsScanning(false);
              return;
            }

            setIsSearching(true);
            activeSearchControllerRef.current?.abort();
            const controller = new AbortController();
            currentSearchRequestIdRef.current = requestId;
            activeSearchControllerRef.current = controller;
            const sku = await processQrCode(scannedCode, { signal: controller.signal });
            if (currentSearchRequestIdRef.current !== requestId) return;
            const response = await runTimedProductSearch(requestId, () =>
              searchProduct(sku, "SKU", zipCode, retailer, withProviderOption({ signal: controller.signal })),
            );
            if (currentSearchRequestIdRef.current !== requestId) return;
            applySearchResponse(response);
            handleCodeChange(scannedCode);


            const successResult = response.results.find((r) => !r.error);
            if (successResult?.sku) {
              if (currentSearchRequestIdRef.current !== requestId) return;
              await checkTrackingStatus(successResult.sku);
            }

            toast.success(t("productSearch.productFound"));
          } catch (error: unknown) {
            if (isAbortError(error)) return;
            if (currentSearchRequestIdRef.current !== requestId) return;
            const message = error instanceof Error ? error.message : t("productSearch.failedToProcessQr");
            setError(message);
            toast.error(message);
          } finally {
            if (activeSearchControllerRef.current) {
              activeSearchControllerRef.current = null;
              setIsSearching(false);
              setShowSearchModal(false);
            }
          }
        } else {
          // Regular barcode scan
          handleCodeChange(scannedCode);
          setIsScanning(false);
          setError(null);
          const codeTypeLabel = searchType === "QR" ? t("productSearch.qrCode") : t("productSearch.barcodeScannedSuccess");
          toast.success(`${codeTypeLabel} ${scannedCode}`);
        }
      })
      .catch(() => {
        // Silent fail - barcode not detected in frame
      });
  }, [searchType, t, zipCode, retailer, isWebcamReady, isScanning, lastScannedCode, runTimedProductSearch]);

  // ============================================
  // Discord & Lead Functions
  // ============================================

  const handleSendToDiscord = () => {
    if (searchResults.length === 0) return;

    const successResult = searchResults.find((result) => !result.error);
    if (!successResult) {
      toast.error(t("searchPage.noResultsForDiscord"));
      return;
    }

    setDiscordProduct(successResult);
    setShowDiscordModal(true);
  };

  const handleAddToLead = () => {
    if (searchResults.length === 0) return;

    const successResult = searchResults.find((result) => !result.error);
    if (!successResult) {
      toast.error(t("searchPage.noResultsForLeads"));
      return;
    }

    setLeadProduct(successResult);
    setShowAddToLeadModal(true);
  };

  const handleShareDeal = async () => {
    if (shareLoading || !firstSuccessfulResult) return;
    setShareLoading(true);
    try {
      const r = firstSuccessfulResult;
      const dealId = await createDeal({
        upc: r.sku || r.barcode, sku: r.barcode || r.sku, name: r.name,
        msrp: r.msrp || 0, onlinePrice: r.onlinePrice || 0,
        storePrice: r.storePrice || 0, image: r.image,
        storeId: r.storeId, retailer,
        category: r.categoryPath || r.mainCategory || "",
        url: "", aisle: r.aisles || "",
        inStock: r.inventory > 0,
        discountPercentage: r.msrp > 0 && r.storePrice < r.msrp
          ? Math.round(((r.msrp - r.storePrice) / r.msrp) * 100) : 0,
      });
      await navigator.clipboard.writeText(getDealShareUrl(dealId));
      toast.success(t("searchPage.shareLinkCopied"));
    } catch { toast.error(t("searchPage.shareLinkFailed")); }
    finally { setShareLoading(false); }
  };

  const handleAffiliateProductClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (affiliateLoading || !firstSuccessfulResult) return;
    const productLink = getRetailerProductUrl(retailer, firstSuccessfulResult.barcode || firstSuccessfulResult.sku);
    const tab = window.open("about:blank", "_blank");
    setAffiliateLoading(true);
    try {
      const trackingUrl = await getAffiliateLink(firstSuccessfulResult.sku || firstSuccessfulResult.barcode, productLink, retailer);
      if (tab) tab.location.href = trackingUrl;
      else window.location.href = trackingUrl;
    } catch {
      if (tab) tab.location.href = productLink;
      else window.location.href = productLink;
    } finally {
      setAffiliateLoading(false);
    }
  };

  // ============================================
  // Search History Functions
  // ============================================

  const loadSearchHistory = useCallback(
    async (reset: boolean = false) => {
      if (isLoadingHistory) return;

      setIsLoadingHistory(true);

      try {
        const cursor = reset ? undefined : historyNextCursor;
        const response: SearchHistoryResponse = await getSearchHistory(20, cursor, retailer);
        const items = response.items || [];

        if (reset) {
          setSearchHistory(items);
        } else {
          setSearchHistory((prev) => [...prev, ...items]);
        }

        setHistoryNextCursor(response.nextCursor);
        setHistoryHasMore(response.hasMore ?? false);
      } catch (err) {
        console.error("Failed to load search history:", err);
        toast.error(tRef.current("searchPage.historyLoadFailed"));
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [isLoadingHistory, historyNextCursor, retailer]
  );

  // ============================================
  // Zip Code Handling
  // ============================================

  const handleZipCodeChange = (newZipCode: string) => {
    setZipCode(newZipCode);
    saveZipCodeStorage(localStorage, newZipCode);
  };

  // An error alone keeps the search box up so the visitor can fix the query and
  // retry; the results view (with only a Back link) was a dead end.
  const hasResultsView = isSearching || showSearchModal || searchResults.length > 0;

  // ============================================
  // Effects
  // ============================================

  // Initialize barcode reader
  useEffect(() => {
    readerRef.current = new BrowserMultiFormatReader();
    return () => {
      if (readerRef.current) {
        readerRef.current.reset();
      }
    };
  }, []);

  // Prevent body scroll
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPriceDropdown(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isScanning) {
        stopScanning();
      }
    };

    if (showPriceDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    if (isScanning) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showPriceDropdown, isScanning]);

  // Scanner interval
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isScanning) {
      interval = setInterval(captureAndScan, 500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isScanning, captureAndScan]);

  // Load zip code on retailer change (deep-link ?zip= overrides storage)
  useEffect(() => {
    const zipParam = searchParams.get("zip")?.trim();
    const fromStorage = getStoredZipCodeStorage(localStorage);
    const nextZip = zipParam || fromStorage;
    setZipCode(nextZip);
    if (zipParam) {
      saveZipCodeStorage(localStorage, zipParam);
    }
  }, [retailer, searchParams]);

  // Load Walmart search providers for admin override
  useEffect(() => {
    if (!showProviderSelector) {
      setSearchProviders([]);
      setSelectedProvider("");
      return;
    }
    let cancelled = false;
    setIsLoadingProviders(true);
    getSearchProviders()
      .then((providers) => {
        if (!cancelled) setSearchProviders(providers);
      })
      .catch(() => {
        if (!cancelled) toast.error(tRef.current("searchPage.providersLoadFailed"));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingProviders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showProviderSelector]);

  // Recent searches come from the server; reload whenever the retailer changes
  useEffect(() => {
    loadSearchHistory(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only on retailer change
  }, [retailer]);

  // Infinite scroll for search history
  useEffect(() => {
    if (hasResultsView) return;

    if (historyObserverRef.current) {
      historyObserverRef.current.disconnect();
    }

    historyObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && historyHasMore && !isLoadingHistory) {
          loadSearchHistory(false);
        }
      },
      { threshold: 0.1 }
    );

    if (historyLoadMoreRef.current) {
      historyObserverRef.current.observe(historyLoadMoreRef.current);
    }

    return () => {
      if (historyObserverRef.current) {
        historyObserverRef.current.disconnect();
      }
    };
  }, [hasResultsView, historyHasMore, isLoadingHistory, loadSearchHistory]);

  // Auto-search from URL params (?zip= fills ZIP for mobile deep links; matches ProductItem nearby flow)
  useEffect(() => {
    const autoSearch = searchParams.get("auto");
    const upcParam = searchParams.get("upc");
    const skuParam = searchParams.get("sku");

    if (autoSearch !== "true" || (!upcParam && !skuParam)) {
      autoSearchHandledRef.current = "";
      return;
    }

    const searchCode = upcParam || skuParam!;
    const searchTypeFromUrl: SearchType = upcParam ? "UPC" : "SKU";

    const zipFromUrl = searchParams.get("zip")?.trim() || "";
    const storedZip = getStoredZipCodeStorage(localStorage).trim();
    const effectiveZip = (zipFromUrl || storedZip).trim();

    if (!effectiveZip) {
      setError(t("searchPage.enterZipToSearch"));
      return;
    }

    const dedupeKey = `${searchCode}|${searchTypeFromUrl}|${effectiveZip}|${retailer}`;
    if (autoSearchHandledRef.current === dedupeKey) return;
    autoSearchHandledRef.current = dedupeKey;

    setCode(searchCode);
    setSearchType(searchTypeFromUrl);

    activeSearchControllerRef.current?.abort();
    const controller = new AbortController();
    const requestId = currentSearchRequestIdRef.current + 1;
    currentSearchRequestIdRef.current = requestId;
    activeSearchControllerRef.current = controller;
    setIsSearching(true);
    setShowSearchModal(true);
    setError(null);
    setSearchResults([]);
    setStoreSort("best_deal");

    void (async () => {
      try {
        const response = await runTimedProductSearch(requestId, () =>
          searchProduct(searchCode, searchTypeFromUrl, effectiveZip, retailer, withProviderOption({
            signal: controller.signal,
          })),
        );
        if (currentSearchRequestIdRef.current !== requestId) return;
        applySearchResponse(response);


        const successResult = response.results.find((r) => !r.error);
        if (successResult?.sku) {
          if (currentSearchRequestIdRef.current !== requestId) return;
          await checkTrackingStatus(successResult.sku);
        }

        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("auto");
        setSearchParams(newSearchParams, { replace: true });
      } catch (err: unknown) {
        if (isAbortError(err)) return;
        if (currentSearchRequestIdRef.current !== requestId) return;
        const message = err instanceof Error ? err.message : t("searchPage.searchFailed");
        setError(message);
        toast.error(message);
      } finally {
        if (activeSearchControllerRef.current === controller) {
          activeSearchControllerRef.current = null;
          setIsSearching(false);
          setShowSearchModal(false);
        }
      }
    })();

    return () => {
      controller.abort();
      autoSearchHandledRef.current = "";
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-triggered search; omitting saveRecentSearch/checkTrackingStatus/setSearchParams avoids rerun loops
  }, [searchParams, retailer]);

  // ============================================
  // Render Helpers
  // ============================================

  const renderSearchTab = () => {
    // Calculate derived values for results
    const isResultInStock = (r: StoreProductResult) =>
      !r.error && (r.status === "IN_STOCK" || (r.inventory !== undefined && r.inventory > 0));
    const inStockWithPrices = enrichedSearchResults.filter(
      (r) => isResultInStock(r) && getStoreSearchDisplayPrice(r) > 0
    );
    const lowestPrice = inStockWithPrices.length > 0 ? Math.min(...inStockWithPrices.map(getStoreSearchDisplayPrice)) : null;
    const isNationwideResults = searchMeta?.scope === "nationwide";
    const getInventoryLabel = (result: StoreProductResult) => {
      if (retailer === "costco") return t("products.inStock");
      if (retailer === "walmart") return `${t("products.onHandLabel")}: ${result.inventory ?? 0}`;
      if (retailer === "bestbuy") return result.inventory > 0 ? `${result.inventory}` : t("products.inStock");
      if (retailer === "samsclub") {
        // Sam's inventory is a stock level (1 limited, 8 plenty), never a count.
        if (result.status?.toUpperCase() === "UNKNOWN") return result.error || t("card.unavailable");
        return samsClubStockLabel(retailer, result.inventory ?? 0, {
          inStock: t("products.inStock"),
          lowStock: t("products.lowStock"),
        }) ?? t("products.outOfStock");
      }
      if (retailer === "tractor_supply") {
        return typeof result.inventory === "number"
          ? t("products.inStockCount", { count: String(result.inventory) })
          : t("products.inStock");
      }
      return `${t("products.salesFloorLabel")}: ${result.inventory ?? 0}${(result.backroom ?? 0) > 0 ? ` | ${t("products.backroomLabel")}: ${result.backroom ?? 0}` : ""}`;
    };

    const getPlaceholder = () => {
      if (retailer === "home_depot") return t("searchPage.placeholderHomeDepot");
      if (retailer === "dollar_general") return t("searchPage.placeholderDollarGeneral");
      if (retailer === "target") return t("searchPage.placeholderTarget");
      if (retailer === "bestbuy") return t("searchPage.placeholderBestBuy");
      if (retailer === "costco") return t("searchPage.placeholderCostco");
      if (retailer === "tractor_supply") return t("searchPage.placeholderTractorSupply");
      if (retailer === "samsclub") return t("searchPage.placeholderSamsClub");
      if (retailer === "amazon") return t("searchPage.placeholderAmazon");
      return t("searchPage.placeholderWalmart");
    };

    const errorCard = error ? (
      <div
        className={`rounded-lg p-3 mb-3 border ${
          error.toLowerCase().includes("maintenance")
            ? "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800"
            : "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800"
        }`}
      >
        <div className="flex items-start gap-2">
          <div
            className={`w-6 h-6 rounded-md flex items-center justify-center ${
              error.toLowerCase().includes("maintenance")
                ? "bg-amber-100 dark:bg-amber-900/50"
                : "bg-red-100 dark:bg-red-900/50"
            }`}
          >
            {error.toLowerCase().includes("maintenance") ? (
              <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
            )}
          </div>
          <div className="flex-1">
            <h3
              className={`text-xs font-semibold mb-0.5 ${
                error.toLowerCase().includes("maintenance")
                  ? "text-amber-900 dark:text-amber-300"
                  : "text-red-900 dark:text-red-300"
              }`}
            >
              {error.toLowerCase().includes("maintenance") ? t("searchPage.serviceMaintenance") : t("searchPage.searchError")}
            </h3>
            <p
              className={`text-xs ${
                error.toLowerCase().includes("maintenance")
                  ? "text-amber-700 dark:text-amber-400"
                  : "text-red-700 dark:text-red-400"
              }`}
            >
              {error}
            </p>
            {isFreeTierSearchLockedError && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <button
                  onClick={() => navigate("/settings?tab=billing&upgradeSource=product_search_error")}
                  className="text-[11px] font-semibold px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  {t("searchPage.unlockLocalSearch")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        {/* Hero + recent searches */}
        {!hasResultsView && (
          <>
            {isFreeUser && (
              <button
                type="button"
                onClick={() => navigate("/settings?tab=billing&upgradeSource=product_search_lock")}
                className="mb-2 flex w-full items-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-left text-xs font-semibold text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/40 dark:text-primary-300"
              >
                <Lock className="h-3.5 w-3.5" />
                {t("searchPage.lockedOnFree")}
              </button>
            )}
            <section className="pb-2 pt-2">
              <div className="mx-auto max-w-3xl">
                <h1 className="text-2xl font-extrabold leading-7 tracking-tight text-gray-900 dark:text-white">{t("searchPage.title")}</h1>
                <p className="mt-1 text-[13.5px] leading-[18px] text-gray-500 dark:text-dark-300">{t("searchPage.subtitle", { retailer: getRetailerDisplayName(retailer) })}</p>

                <form onSubmit={handleSearch}>
                  {searchType === "QR" ? (
                    <button
                      type="button"
                      onPointerDown={() => startScanning()}
                      disabled={isSearching || isScanning || isFreeUser}
                      className="mt-3.5 inline-flex h-[54px] w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-900 shadow-sm transition-all hover:bg-gray-50 disabled:opacity-60 dark:border-dark-600 dark:bg-dark-800 dark:text-white"
                    >
                      <Camera className="h-4 w-4" />
                      {isScanning ? t("productSearch.scanning") : t("productSearch.scanQrButton")}
                    </button>
                  ) : (
                    <div className="mt-3.5 flex h-[54px] items-center gap-2.5 rounded-2xl border border-gray-200 bg-white pl-4 pr-1.5 shadow-sm focus-within:border-primary-400 focus-within:ring-4 focus-within:ring-primary-100 dark:border-dark-600 dark:bg-dark-800 dark:focus-within:ring-primary-950">
                      <Search className="h-[19px] w-[19px] flex-shrink-0 text-gray-400" />
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => handleCodeChange(e.target.value)}
                        placeholder={getPlaceholder()}
                        inputMode={retailer === "target" || retailer === "bestbuy" || retailer === "costco" ? "numeric" : "text"}
                        className="h-full min-w-0 flex-1 bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400 disabled:opacity-60 dark:text-white dark:placeholder:text-dark-400"
                        disabled={isSearching || isFreeUser}
                      />
                      {code.length > 0 && (
                        <button
                          type="button"
                          onClick={handleReset}
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:text-gray-600"
                          aria-label={t("searchPage.clearSearch")}
                        >
                          <X className="h-[18px] w-[18px]" />
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={isSearching || !code.trim() || !zipCode.trim() || detectedCodeType === "Unknown" || isFreeUser}
                        className="inline-flex h-[42px] min-w-[78px] flex-shrink-0 items-center justify-center rounded-xl bg-primary-600 px-4 text-sm font-bold text-white transition-all hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isSearching ? <Loader className="h-4 w-4 animate-spin" /> : t("common.search")}
                      </button>
                    </div>
                  )}
                  {detectedCodeType !== "Unknown" && code.trim() && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-dark-300">{t("searchPage.detected", { type: retailer === "target" && detectedCodeType === "SKU" ? "TCIN" : detectedCodeType })}</p>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) handleBarcodePhoto(file);
                    }}
                  />
                  <div className="mt-2.5 flex gap-2">
                    <button
                      type="button"
                      onPointerDown={() => startScanning()}
                      disabled={isSearching || isScanning || isFreeUser}
                      className="inline-flex h-10 flex-1 items-center justify-center gap-[7px] rounded-xl border border-gray-200 bg-gray-50 text-[13.5px] font-semibold text-gray-800 transition hover:bg-gray-100 disabled:opacity-60 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-100 dark:hover:bg-dark-700"
                      aria-label={t("searchPage.scanBarcode")}
                    >
                      <ScanBarcode className="h-[17px] w-[17px]" />
                      {t("productSearch.scan")}
                    </button>
                    <button
                      type="button"
                      onPointerDown={() => photoInputRef.current?.click()}
                      disabled={isSearching || isScanning || isDecodingPhoto || isFreeUser}
                      className="inline-flex h-10 flex-1 items-center justify-center gap-[7px] rounded-xl border border-gray-200 bg-gray-50 text-[13.5px] font-semibold text-gray-800 transition hover:bg-gray-100 disabled:opacity-60 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-100 dark:hover:bg-dark-700"
                      aria-label={t("searchPage.uploadBarcodePhoto")}
                    >
                      {isDecodingPhoto ? <Loader className="h-[17px] w-[17px] animate-spin" /> : <ImageUp className="h-[17px] w-[17px]" />}
                      {t("searchPage.photo")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsZipEditing((open) => !open)}
                      className="inline-flex h-10 items-center justify-center gap-[7px] rounded-xl border border-gray-200 bg-gray-50 px-3 text-[13.5px] font-semibold text-gray-800 transition hover:bg-gray-100 dark:border-dark-600 dark:bg-dark-800 dark:text-dark-100 dark:hover:bg-dark-700"
                      aria-label={t("searchPage.setZipCode")}
                      aria-expanded={isZipEditing || !zipCode.trim()}
                    >
                      <MapPin className="h-[15px] w-[15px]" />
                      {zipCode.trim() || t("searchPage.setZip")}
                    </button>
                  </div>
                  {(isZipEditing || !zipCode.trim()) && (
                    <div className="mt-2.5 flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 dark:border-dark-600 dark:bg-dark-800">
                      <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      <input
                        type="text"
                        value={zipCode}
                        onChange={(e) => handleZipCodeChange(e.target.value)}
                        onBlur={() => setIsZipEditing(false)}
                        placeholder={t("searchPage.zipPlaceholder")}
                        className="h-full min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-dark-400"
                        disabled={isSearching}
                        maxLength={5}
                        pattern="[0-9]{5}"
                        inputMode="numeric"
                        autoFocus={isZipEditing}
                      />
                    </div>
                  )}
                </form>
              </div>
            </section>

            {errorCard && <div className="mx-auto mt-3 w-full max-w-3xl">{errorCard}</div>}

            {showHDScopeSelector && (
              <div className="mx-auto mt-3 flex w-full max-w-3xl items-center gap-2">
                <span className="text-[12.5px] text-gray-500 dark:text-dark-300">{t("searchPage.scope")}</span>
                <div className="flex gap-0.5 rounded-[9px] bg-gray-100 p-0.5 dark:bg-dark-800">
                  {(["local", "nationwide"] as HomeDepotSearchScope[]).map((scope) => {
                    const active = hdSearchScope === scope;
                    return (
                      <button
                        key={scope}
                        type="button"
                        disabled={isSearching}
                        onClick={() => setHdSearchScope(scope)}
                        className={`h-[26px] rounded-[7px] px-3 text-xs font-bold transition ${active ? "bg-white text-primary-700 shadow-sm dark:bg-dark-900 dark:text-primary-300" : "text-gray-600 dark:text-dark-200"}`}
                      >
                        {scope === "local" ? t("searchPage.myStores") : t("searchPage.nationwide")}
                      </button>
                    );
                  })}
                </div>
                <span className="ml-auto text-[11.5px] text-gray-400 dark:text-dark-400">{t("searchPage.adminOnly")}</span>
              </div>
            )}

            {showProviderSelector && (
              <div className="mx-auto mt-3 w-full max-w-3xl rounded-xl border border-gray-200 p-3 dark:border-dark-700">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12.5px] font-semibold text-gray-700 dark:text-dark-100">{t("searchPage.dataSource")}</span>
                  <span className="text-[11.5px] text-gray-400 dark:text-dark-400">{t("searchPage.adminOnly")}</span>
                </div>
                <select
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={isSearching || isLoadingProviders}
                  className="mt-2 min-h-[40px] w-full rounded-lg border border-gray-200 bg-gray-50/70 px-3 text-sm text-gray-950 focus:border-primary-400 focus:bg-white focus:outline-none dark:border-dark-600 dark:bg-dark-800 dark:text-white"
                >
                  <option value="">
                    {user?.walmartProviderOverride
                      ? t("searchPage.userOverride", { provider: user.walmartProviderOverride })
                      : t("searchPage.defaultProvider")}
                  </option>
                  {searchProviders.map((provider) => (
                    <option key={provider.type} value={provider.type}>
                      {provider.label} ({provider.type}){provider.enabled ? "" : ` — ${t("searchPage.providerDisabled")}`}
                    </option>
                  ))}
                </select>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-dark-100">
                  <input
                    type="checkbox"
                    checked={bypassCache}
                    onChange={(e) => setBypassCache(e.target.checked)}
                    disabled={isSearching}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-700"
                  />
                  {t("searchPage.bypassCache")}
                </label>
              </div>
            )}

            <div className="mx-auto w-full max-w-3xl">
              <h2 className="pb-0.5 pt-5 text-[17px] font-bold tracking-tight text-gray-900 dark:text-white">{t("searchPage.recent")}</h2>
              {isLoadingHistory && searchHistory.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <Loader className="h-6 w-6 animate-spin text-primary-600" />
                </div>
              ) : searchHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <Clock className="h-10 w-10 text-gray-300 dark:text-dark-600" />
                  <p className="mt-4 text-base font-medium text-gray-500 dark:text-dark-300">{t("searchPage.noSearchesYet")}</p>
                  <p className="mt-1 text-[13px] text-gray-400 dark:text-dark-400">{t("searchPage.noSearchesYetHint")}</p>
                </div>
              ) : (
                <>
                  {searchHistory.map((item, index) => {
                    const results = item.results || [];
                    const inStockResults = results.filter((r) => !r.error && (r.status === "IN_STOCK" || (r.inventory !== undefined && r.inventory > 0)));
                    const inStockWithPrices = inStockResults.filter((r) => getStoreSearchDisplayPrice(r) > 0);
                    const lowestPrice = inStockWithPrices.length > 0 ? Math.min(...inStockWithPrices.map(getStoreSearchDisplayPrice)) : null;
                    const storesInStock = inStockResults.length;
                    const totalStores = results.filter((r) => !r.error).length;
                    const stockLabel = storesInStock > 0 ? t("searchPage.stockOf", { inStock: storesInStock, total: totalStores }) : totalStores > 0 ? t("searchPage.outOfStock") : t("searchPage.noResults");
                    const stockClass = storesInStock > 0 ? "bg-emerald-500 text-emerald-600 dark:text-emerald-400" : totalStores > 0 ? "bg-red-500 text-red-600 dark:text-red-400" : "bg-gray-400 text-gray-500 dark:text-dark-300";
                    const displayName = item.name || t("searchPage.unknownProduct");
                    return (
                      <div key={`${item.timestamp}-${index}`} className="flex items-center gap-2.5 border-b border-gray-100 py-[11px] dark:border-dark-800">
                        <button
                          type="button"
                          onClick={() => handleViewHistoryResults(item)}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          aria-label={t("searchPage.viewResultsFor", { name: item.name || item.sku })}
                        >
                          {item.imageUrl ? (
                            <img src={getProductImageUrl(item.imageUrl, "400")} alt="" className="h-12 w-12 flex-shrink-0 rounded-xl bg-gray-100 object-contain dark:bg-dark-800" />
                          ) : (
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-dark-800">
                              <Package className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="line-clamp-2 text-sm font-semibold leading-[18px] text-gray-900 dark:text-white">{displayName}</p>
                            <p className={`mt-[3px] flex items-center gap-1.5 text-[12.5px] ${stockClass.split(" ").slice(1).join(" ")}`}>
                              <span className={`inline-block h-1.5 w-1.5 rounded-full ${stockClass.split(" ")[0]}`} aria-hidden="true" />
                              <span className="font-semibold">{stockLabel}</span>
                              <span className="text-gray-500 dark:text-dark-300">· {formatTimestamp(item.timestamp)}</span>
                            </p>
                          </div>
                          {lowestPrice !== null && (
                            <span className="flex-shrink-0 text-[15px] font-extrabold tracking-tight text-gray-900 dark:text-white">${lowestPrice.toFixed(2)}</span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSearchAgainFromHistory(item)}
                          disabled={isSearching}
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700 transition hover:bg-primary-100 disabled:opacity-50 dark:bg-primary-500/20 dark:text-primary-300"
                          aria-label={t("searchPage.searchAgainFor", { name: item.name || item.sku })}
                        >
                          <Search className="h-[15px] w-[15px]" />
                        </button>
                      </div>
                    );
                  })}
                  <div ref={historyLoadMoreRef} className="flex justify-center py-4">
                    {isLoadingHistory && <Loader className="h-5 w-5 animate-spin text-primary-600" />}
                    {!isLoadingHistory && !historyHasMore && searchHistory.length > 0 && (
                      <p className="text-xs text-gray-400 dark:text-dark-500">{t("searchPage.noMoreResults")}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Scanner Modal */}
        {isScanning && (
          <div
            className="fixed inset-0 z-50 bg-[#04050a]"
            onPointerDown={stopScanning}
          >
            <div
              className="flex h-full w-full flex-col text-white"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {/* Scanner Header */}
              <div className="flex items-center justify-between px-4 pb-3 pt-4 sm:px-6">
                <div className="min-w-0">
                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">
                    {t("searchPage.liveScanner")}
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {searchType === "QR" ? t("productSearch.scanQrTitle") : t("productSearch.scanBarcodeModalTitle")}
                  </h3>
                  <p className="mt-1 text-sm text-white/60">
                    {searchType === "QR"
                      ? t("searchPage.scannerHintQr")
                      : t("searchPage.scannerHintBarcode")}
                  </p>
                </div>
                <button
                  onPointerDown={stopScanning}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/6 text-white/80 transition-all hover:bg-white/12 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scanner Body */}
              <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 sm:px-6 sm:pb-6">
                <div className="relative min-h-0 flex-1 overflow-hidden rounded-[28px] border border-white/10 bg-black shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
                  <div className="absolute inset-0">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      className="h-full w-full object-cover"
                      videoConstraints={{ width: 720, height: 720, facingMode: "environment" }}
                      onUserMedia={handleUserMedia}
                      onUserMediaError={handleUserMediaError}
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(4,5,10,0.72),rgba(4,5,10,0.18)_22%,rgba(4,5,10,0.18)_78%,rgba(4,5,10,0.84))]" />
                    <div className="absolute left-1/2 top-1/2 h-[32%] w-[82%] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-[28px] border border-white/70 shadow-[0_0_0_9999px_rgba(4,5,10,0.5)]">
                      <div className="absolute inset-x-6 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-primary-300 to-transparent shadow-[0_0_16px_rgba(196,181,253,0.8)] animate-pulse" />
                      <div className="absolute left-4 top-4 h-8 w-8 rounded-tl-2xl border-l-[3px] border-t-[3px] border-primary-300" />
                      <div className="absolute right-4 top-4 h-8 w-8 rounded-tr-2xl border-r-[3px] border-t-[3px] border-primary-300" />
                      <div className="absolute bottom-4 left-4 h-8 w-8 rounded-bl-2xl border-b-[3px] border-l-[3px] border-primary-300" />
                      <div className="absolute bottom-4 right-4 h-8 w-8 rounded-br-2xl border-b-[3px] border-r-[3px] border-primary-300" />
                      {searchType === "QR" && (
                        <div className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/45" />
                      )}
                    </div>
                    <div className="absolute left-4 right-4 top-4 flex items-start justify-between sm:left-6 sm:right-6">
                      <div className="rounded-full border border-white/12 bg-black/30 px-3 py-1.5 backdrop-blur-md">
                        <div className="flex items-center gap-2 text-[11px] font-medium text-white/78">
                          <div className={`h-2 w-2 rounded-full ${isWebcamReady ? "bg-emerald-400" : "bg-amber-300 animate-pulse"}`} />
                          <span>{isWebcamReady ? t("searchPage.cameraReady") : t("searchPage.initializingCamera")}</span>
                        </div>
                      </div>
                      <div className="hidden rounded-full border border-white/12 bg-black/30 px-3 py-1.5 text-[11px] font-medium text-white/70 backdrop-blur-md sm:block">
                        {searchType === "QR" ? t("searchPage.squareFocus") : t("searchPage.horizontalBarcodeFocus")}
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                    <div className="rounded-[24px] border border-white/10 bg-black/38 p-4 backdrop-blur-xl">
                      {cameraError ? (
                        <div className="rounded-2xl border border-red-400/25 bg-red-500/10 p-3">
                          <p className="mb-1 text-sm font-semibold text-red-200">{t("searchPage.cameraErrorTitle")}</p>
                          <p className="text-sm text-red-100/85">{cameraError}</p>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                          <div className="max-w-md">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/45">
                              {t("searchPage.guidance")}
                            </div>
                            <p className="mt-2 text-sm leading-6 text-white/82">
                              {searchType === "QR"
                                ? t("searchPage.guidanceQr")
                                : t("searchPage.guidanceBarcode")}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="rounded-full border border-white/12 bg-white/6 px-3 py-2 text-xs font-medium text-white/78">
                              {isWebcamReady ? t("searchPage.scanningLive") : t("searchPage.preparingScanner")}
                            </div>
                            <button
                              onPointerDown={stopScanning}
                              className="pointer-events-auto inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-white/15 bg-white px-4 text-sm font-semibold text-gray-950 transition-all hover:bg-white/90"
                            >
                              <X className="h-4 w-4" />
                              {t("common.close")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasResultsView && (
          <div className="mb-5 border-b border-gray-200 pb-4 dark:border-dark-700">
            <button
              type="button"
              onPointerDown={handleBackToSearch}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 transition-colors hover:text-gray-950 dark:text-dark-100 dark:hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.back")}
            </button>
          </div>
        )}

        {/* Loading State */}
        {(isSearching || showSearchModal) && (
          <div className="space-y-5">
            <div className="text-[11px] font-medium text-gray-500 dark:text-dark-300">
              {t("products.scanningForUpdates")}{" "}
              {hdSearchScope === "nationwide" && showHDScopeSelector
                ? t("searchPage.hdNationwideSearching")
                : t("common.searchingMessage")}
            </div>
            <section className="overflow-hidden rounded-2xl border border-amber-200/70 bg-amber-50/80 shadow-sm dark:border-amber-700/40 dark:bg-amber-950/20">
              <div className="border-b border-amber-200/70 px-4 py-3 dark:border-amber-800/50">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">
                  {t("products.nearbyTipsTitle")}
                </p>
              </div>
              <div className="divide-y divide-amber-200/70 dark:divide-amber-800/50">
                {nearbyLoadingTips.map((tip, index) => (
                  <div key={tip} className="flex gap-3 px-4 py-3">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-amber-700 shadow-sm ring-1 ring-amber-200 dark:bg-amber-900/50 dark:text-amber-200 dark:ring-amber-700/50">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-5 text-amber-900 dark:text-amber-100">
                      {tip}
                    </p>
                  </div>
                ))}
              </div>
            </section>
            <div className="animate-pulse space-y-5">
            <section className="border-b border-gray-200 pb-5 dark:border-dark-700">
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 rounded-md bg-gray-100 dark:bg-dark-800" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-dark-700" />
                  <div className="h-3 w-1/2 rounded bg-gray-100 dark:bg-dark-800" />
                  <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4 dark:border-dark-700 sm:grid-cols-4">
                    <div className="space-y-2">
                      <div className="h-2 w-16 rounded bg-gray-100 dark:bg-dark-800" />
                      <div className="h-4 w-20 rounded bg-gray-200 dark:bg-dark-700" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-12 rounded bg-gray-100 dark:bg-dark-800" />
                      <div className="h-4 w-16 rounded bg-gray-200 dark:bg-dark-700" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-14 rounded bg-gray-100 dark:bg-dark-800" />
                      <div className="h-4 w-20 rounded bg-gray-200 dark:bg-dark-700" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="border-b border-gray-200 pb-3 dark:border-dark-700">
                <div className="h-3 w-28 rounded bg-gray-100 dark:bg-dark-800" />
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="h-7 w-20 rounded-md bg-gray-100 dark:bg-dark-800" />
                <div className="h-7 w-20 rounded-md bg-gray-100 dark:bg-dark-800" />
                <div className="h-7 w-24 rounded-md bg-gray-100 dark:bg-dark-800" />
              </div>
              <div className="border-t border-gray-200 pt-4 dark:border-dark-700">
                <div className="flex items-center justify-between border-b border-gray-200 pb-3 dark:border-dark-700">
                  <div className="space-y-2">
                    <div className="h-2 w-16 rounded bg-gray-100 dark:bg-dark-800" />
                    <div className="h-4 w-28 rounded bg-gray-200 dark:bg-dark-700" />
                  </div>
                  <div className="h-3 w-20 rounded bg-gray-100 dark:bg-dark-800" />
                </div>
                <div className="space-y-0">
                  {[0, 1, 2].map((row) => (
                    <div key={row} className="flex items-start justify-between gap-3 border-b border-gray-200 py-4 dark:border-dark-700">
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-20 rounded bg-gray-200 dark:bg-dark-700" />
                        <div className="h-3 w-48 rounded bg-gray-100 dark:bg-dark-800" />
                      </div>
                      <div className="w-20 space-y-2">
                        <div className="h-2 w-10 rounded bg-gray-100 dark:bg-dark-800 ml-auto" />
                        <div className="h-4 w-16 rounded bg-gray-200 dark:bg-dark-700 ml-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
            </div>
          </div>
        )}

        {isAdmin && clientSearchDurationMs !== null && !isSearching && !showSearchModal && (
          <div
            data-testid="client-search-duration"
            className="mb-3 flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-800 dark:bg-sky-950/30 dark:text-sky-200"
          >
            <Clock className="h-3.5 w-3.5" />
            {t("searchPage.clientSearchTime")} <span className="font-mono font-bold">{formatClientSearchDuration(clientSearchDurationMs, t)}</span>
          </div>
        )}

        {/* Error Display (with no results the search box stays up, above) */}
        {hasResultsView && !isSearching && !showSearchModal && errorCard}

        {/* Search Results */}
        {!isSearching && !showSearchModal && searchResults.length > 0 && (
          <div className="flex-1 min-h-0">
            {!firstSuccessfulResult ? (
              // No successful results
              <div className="border-b border-gray-200 pb-5 dark:border-dark-700">
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-900/20">
                    <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-950 dark:text-white">
                      {t("productSearch.productNotFound")}
                    </h2>
                    <p className="text-xs text-gray-700 dark:text-dark-100">{t("searchPage.unableToFindAtStores")}</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {searchResults.map((result, index) => (
                    <div
                      key={`error-${result.storeId}-${index}`}
                      className="flex items-center gap-2 rounded-md border border-red-100 bg-red-50 px-2.5 py-2 text-xs dark:border-red-900/40 dark:bg-red-900/20"
                    >
                      <MapPin className="w-3.5 h-3.5 text-red-500 dark:text-red-400 flex-shrink-0" />
                      <span className="font-medium text-gray-900 dark:text-white">#{result.storeId}</span>
                      <span className="text-red-600 dark:text-red-400 truncate">{result.error || t("searchPage.notFound")}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Successful results
              <section>
                {/* Product Hero Card */}
                <div className="border-b border-gray-100 bg-white px-4 pb-3 pt-3 dark:border-dark-700 dark:bg-dark-800 sm:px-5">
                  <div className="flex items-start gap-2.5">
                    {/* Product Image */}
                    <div className="relative flex-shrink-0">
                      {firstSuccessfulResult.image ? (
                        <img
                          src={getProductImageUrl(firstSuccessfulResult.image, "400")}
                          alt=""
                          className="h-11 w-11 rounded-lg bg-white object-contain dark:bg-dark-700"
                        />
                      ) : (
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gray-100 dark:bg-dark-700">
                          <Package className="h-5 w-5 text-gray-400 dark:text-dark-400" />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h2 className="line-clamp-2 text-sm font-semibold leading-5 text-gray-900 dark:text-white">
                        {firstSuccessfulResult.name}
                      </h2>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        {firstSuccessfulResult.brand && (
                          <span className="text-[10px] leading-4 text-gray-500 dark:text-dark-200">
                            {firstSuccessfulResult.brand}
                          </span>
                        )}
                        <span className="text-[10px] leading-4 text-gray-500 dark:text-dark-200">
                          <span className="font-semibold">
                            {retailer === "home_depot" ? "ID" : retailer === "target" ? "TCIN" : retailer === "costco" ? t("searchPage.itemLabel") : retailer === "amazon" ? "ASIN" : "SKU"}:
                          </span>{" "}
                          {firstSuccessfulResult.barcode}
                        </span>
                        {retailer !== "target" && firstSuccessfulResult.sku && (
                          <span className="text-[10px] leading-4 text-gray-500 dark:text-dark-200">
                            <span className="font-semibold">UPC:</span>{" "}
                            {firstSuccessfulResult.sku}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Price Stat Tiles */}
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {lowestPrice && (
                      <div className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        <span>{t("searchPage.bestStore")}</span>
                        <span>${lowestPrice.toFixed(2)}</span>
                      </div>
                    )}
                    {retailer !== "target" && !!firstSuccessfulResult?.msrp && (
                      <div className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:bg-dark-700 dark:text-dark-200">
                        <span>MSRP</span>
                        <span>${firstSuccessfulResult.msrp.toFixed(2)}</span>
                      </div>
                    )}
                    {firstSuccessfulResult.onlinePrice > 0 && (
                      <div className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 dark:bg-dark-700 dark:text-dark-200">
                        <span>{t("searchPage.online")}</span>
                        <span>${firstSuccessfulResult.onlinePrice.toFixed(2)}</span>
                      </div>
                    )}
                    {amazonData?.price != null && amazonData.price > 0 && (
                      amazonData.url ? (
                        <a
                          href={amazonData.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700 transition-colors hover:bg-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:hover:bg-orange-900/60"
                        >
                          <span className="flex items-center gap-1">
                            <span>Amazon</span>
                            <ExternalLink className="h-2.5 w-2.5" />
                          </span>
                          <span>${amazonData.price.toFixed(2)}</span>
                          <span className="flex items-baseline gap-1.5">
                            {amazonData.isPrime && <span className="text-[9px] font-bold text-blue-600 dark:text-blue-300">PRIME</span>}
                          </span>
                        </a>
                      ) : (
                        <div className="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                          <span>Amazon</span>
                          <span>${amazonData.price.toFixed(2)}</span>
                          <span className="flex items-baseline gap-1.5">
                            {amazonData.isPrime && <span className="text-[9px] font-bold text-blue-600 dark:text-blue-300">PRIME</span>}
                          </span>
                        </div>
                      )
                    )}
                  </div>

                  {/* Walmart Barcode */}
                  {retailer === "walmart" && (() => {
                    const barcodeResult = searchResults.find((r) => !r.error && r.barcode);
                    return barcodeResult?.barcode ? (
                      <div className="mt-2 flex items-center justify-center overflow-x-auto rounded-lg border border-gray-100 bg-white p-3 dark:border-dark-700">
                        <Barcode value={barcodeResult.barcode} width={1.5} height={40} fontSize={11} margin={0} />
                      </div>
                    ) : null;
                  })()}

                  {/* Action Buttons */}
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <button
                      onPointerDown={handleTrackToggle}
                      disabled={isTrackingProduct}
                      className={`inline-flex min-h-8 items-center gap-1 rounded-lg px-2.5 text-[11px] font-semibold transition-colors ${
                        isTracked
                          ? "bg-emerald-500 text-white hover:bg-emerald-600"
                          : "bg-gray-900 text-white hover:bg-gray-800 dark:bg-primary-600 dark:hover:bg-primary-700"
                      } ${isTrackingProduct ? "opacity-50" : ""}`}
                    >
                      {isTracked ? <Zap className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                      {isTracked ? t("products.tracking") : t("products.track")}
                    </button>

                    <SaveButton
                      productId={firstSuccessfulResult.sku}
                      productName={firstSuccessfulResult.name}
                      productImage={firstSuccessfulResult.image}
                      productCategory={firstSuccessfulResult.mainCategory || firstSuccessfulResult.categoryPath}
                      className="min-h-8 justify-center rounded-lg border-0 bg-rose-50 px-2.5 text-[11px] font-semibold text-rose-700 transition-colors hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 dark:hover:bg-rose-900/50"
                    />

                    {(retailer === "walmart" || retailer === "target") ? (
                      <button
                        onClick={handleAffiliateProductClick}
                        disabled={affiliateLoading}
                        className="inline-flex min-h-8 items-center gap-1 rounded-lg bg-blue-50 px-2.5 text-[11px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-60 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                      >
                        {affiliateLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                        {getRetailerLinkLabel(retailer)}
                      </button>
                    ) : (
                      <a
                        href={getRetailerProductUrl(retailer, firstSuccessfulResult.barcode || firstSuccessfulResult.sku)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-8 items-center gap-1 rounded-lg bg-blue-50 px-2.5 text-[11px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {getRetailerLinkLabel(retailer)}
                      </a>
                    )}

                    <button
                      onPointerDown={handleShareDeal}
                      disabled={shareLoading}
                      className="inline-flex min-h-8 items-center gap-1 rounded-lg bg-gray-50 px-2 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-60 dark:bg-dark-700 dark:text-dark-200 dark:hover:bg-dark-600"
                    >
                      {shareLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
                      {t("searchPage.share")}
                    </button>

                    {showAddToLead && (
                      <button
                        onPointerDown={handleAddToLead}
                        className="inline-flex min-h-8 items-center gap-1 rounded-lg bg-amber-100 px-2.5 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t("searchPage.lead")}
                      </button>
                    )}
                  </div>

                  {/* Admin Actions */}
                  {(user?.admin || user?.allowSendToDiscord) && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-dashed border-gray-200 pt-3 dark:border-dark-600">
                      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-gray-400 dark:text-dark-400">{t("searchPage.admin")}</span>
                      <button
                        onPointerDown={handleSendToDiscord}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-purple-100 px-2.5 py-1 text-[11px] font-semibold text-purple-700 transition-colors hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-900/60"
                      >
                        <MessageSquare className="h-3 w-3" />
                        Discord
                      </button>
                    </div>
                  )}
                </div>

                {/* Store List */}
                <div>
                  {isNationwideResults ? (
                    <VirtualizedStoreSearchList
                      results={enrichedSearchResults}
                      retailer={retailer}
                      storeSort={storeSort}
                      onSortChange={setStoreSort}
                      storeFilter={hdStoreFilter}
                      onStoreFilterChange={setHdStoreFilter}
                      inStockOnly={hdInStockOnly}
                      onInStockOnlyChange={setHdInStockOnly}
                      getInventoryLabel={getInventoryLabel}
                      exportFilenameBase={`hd-nationwide-${firstSuccessfulResult?.sku || "search"}`}
                      meta={searchMeta ?? undefined}
                      t={t}
                    />
                  ) : (
                    <NearbyStoreResultsList
                      results={enrichedSearchResults}
                      retailer={retailer}
                      storeSort={storeSort}
                      onSortChange={setStoreSort}
                      productMsrp={firstSuccessfulResult?.msrp}
                      scrollable={false}
                    />
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </>
    );
  };

  // ============================================
  // Main Render
  // ============================================

  return (
    <div className="h-full overflow-hidden bg-white dark:bg-dark-950">
      <div ref={scrollContainerRef} className="mx-auto flex h-full min-h-0 max-w-5xl flex-col overflow-y-auto px-4 py-4 sm:px-6 lg:px-8">
        {renderSearchTab()}

        {/* Modals */}
        <DiscordPriceModal
          isOpen={showDiscordModal}
          onClose={() => {
            setShowDiscordModal(false);
            setDiscordProduct(null);
          }}
          product={discordProduct}
          retailer={retailer}
          zipCode={zipCode}
        />

        <AddToLeadModal
          isOpen={showAddToLeadModal}
          onClose={() => {
            setShowAddToLeadModal(false);
            setLeadProduct(null);
          }}
          product={leadProduct}
          retailer={retailer}
        />

        <ProductSearchTrackingModal
          isOpen={showTrackingModal}
          onClose={() => setShowTrackingModal(false)}
          onSubmit={handleAddTracker}
          isLoading={isTrackingProduct}
          productUpc={firstSuccessfulResult?.sku}
          searchZipCode={zipCode}
          productName={firstSuccessfulResult?.name}
          productImage={firstSuccessfulResult?.image}
          productCategory={firstSuccessfulResult?.mainCategory || firstSuccessfulResult?.categoryPath}
          msrp={firstSuccessfulResult?.msrp}
          currentPrice={firstSuccessfulResult?.storePrice}
        />

      </div>
    </div>
  );
}
